// Lib
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import ResponsiveProgressiveImage from '../../../../../../components/images/ResponsiveProgressiveImage';
import DraggableElementCreationTool from '../../../../../../element/dnd/DraggableElementCreationTool';
import TooltipSource from '../../../../../../components/tooltips/TooltipSource';

import { createCaptionJsonContent } from '../../../../../../../common/tiptap/utils/createJsonContentUtils/createJsonContent';
import convertHtmlToDraft from '../../../../../../../common/utils/editor/convertHtmlToDraft';
import { asObject, prop } from '../../../../../../../common/utils/immutableHelper';

import { ExperimentId } from '../../../../../../../common/experiments/experimentsConstants';
import { getIsFeatureEnabledForCurrentUser } from '../../../../../../element/feature/elementFeatureSelector';

import { getElementBoundingRect } from '../../../../../../utils/domUtil';
import { getScaledGrabOffset } from '../../../../../../utils/dnd/dragAndDropUtils';

import { IMAGE_TYPES } from '../../../../../../../common/media/mediaConstants';
import { TooltipPositions } from '../../../../../../components/tooltips/tooltipConstants';
import { TIMES } from '../../../../../../../common/utils/timeUtil';
import { ELEMENT_CREATION_SOURCES, ELEMENT_DEFAULT_WIDTH } from '../../../../../../../common/elements/elementConstants';
import { ElementType } from '../../../../../../../common/elements/elementTypes';

import './ImageSearchResult.scss';

const getElementScaledCustomDragOffset = () => ({ x: 0, y: 0 });

const getElementScaledGrabOffset = (props, monitor) => {
    const sourceClientOffset = monitor.getSourceClientOffset();
    const clientOffset = monitor.getClientOffset();

    return getScaledGrabOffset({ clientOffset, sourceClientOffset });
};

const getKeywords = prop('keywords');
const getImage = prop('image');
const getCaption = prop('caption');

const getDefaultContent =
    (converter) =>
    ({ result }) => ({
        image: asObject(getImage(result)),
        keywords: asObject(getKeywords(result)),
        caption: converter(getCaption(result)),
        showCaption: true,
    });

// TODO-REMOVE-DRAFT: Remove this logic when removing draft support
const getDefaultTiptapContent = getDefaultContent(createCaptionJsonContent);
const getDefaultDraftContent = getDefaultContent(convertHtmlToDraft);

const ImageSearchResult = (props) => {
    const wrapperRef = useRef();
    const { result, gridSize } = props;

    const calculateTargetRect = () => getElementBoundingRect(wrapperRef.current);
    const shouldUseTiptap = useSelector(getIsFeatureEnabledForCurrentUser(ExperimentId.tiptapConversion));

    return (
        <li className="ImageSearchResult" ref={wrapperRef}>
            <TooltipSource
                tooltipText="Drag me"
                position={TooltipPositions.RIGHT}
                distance={1}
                duration={TIMES.SECOND * 3}
                enabled
                fade
                triggerOnClick
                getTargetRectFn={calculateTargetRect}
            >
                <div className="image-container">
                    <DraggableElementCreationTool
                        {...props}
                        delayTouchStart={300}
                        getElementScaledCustomDragOffset={getElementScaledCustomDragOffset}
                        getElementScaledGrabOffset={getElementScaledGrabOffset}
                        elementType={ElementType.IMAGE_TYPE}
                        getDefaultContent={shouldUseTiptap ? getDefaultTiptapContent : getDefaultDraftContent}
                        creationSource={ELEMENT_CREATION_SOURCES.IMAGE_SEARCH}
                        editOnCreate={false}
                        selectOnCreate={false}
                    >
                        <ResponsiveProgressiveImage
                            imageDetails={prop('image', result)}
                            widthPx={ELEMENT_DEFAULT_WIDTH * gridSize}
                            imageType={IMAGE_TYPES.ELEMENT}
                        />
                    </DraggableElementCreationTool>
                </div>
            </TooltipSource>
        </li>
    );
};

ImageSearchResult.propTypes = {
    result: PropTypes.object,
    gridSize: PropTypes.number.isRequired,
    closeImagePopup: PropTypes.func,
    fetchImageColorPallete: PropTypes.func,
};

export default ImageSearchResult;
